/**
 * Spanish A1 Sentence Builders
 * Chunk-based language frames following Conti's EPI approach.
 * Each sentence builder targets specific communicative functions.
 */

import type { SentenceBuilder } from '@/types/language';

export const SPANISH_A1_SENTENCE_BUILDERS: SentenceBuilder[] = [
  // ── SB-01: Introduction (Presentarse) ─────────────────────────
  {
    id: 'sb-es-a1-01',
    title: 'Introduccion',
    columns: [
      {
        name: 'Saludo (Greeting)',
        options: ['Hola', 'Buenos dias', 'Buenas tardes', 'Buenas noches'],
        translations: {
          'Hola': 'Hello',
          'Buenos dias': 'Good morning',
          'Buenas tardes': 'Good afternoon',
          'Buenas noches': 'Good evening/night',
        },
      },
      {
        name: 'Sujeto (Subject)',
        options: ['Me', 'Yo', 'Mi nombre', 'Este'],
        translations: {
          'Me': 'Myself',
          'Yo': 'I',
          'Mi nombre': 'My name',
          'Este': 'This is',
        },
      },
      {
        name: 'Verbo (Verb)',
        options: ['llamo', 'soy', 'es', 'presento a'],
        translations: {
          'llamo': 'call (myself)',
          'soy': 'am',
          'es': 'is',
          'presento a': 'introduce',
        },
      },
      {
        name: 'Complemento (Object)',
        options: ['Juan', 'Maria', 'mi amigo', 'mi amiga', 'el profesor'],
        translations: {
          'Juan': 'Juan',
          'Maria': 'Maria',
          'mi amigo': 'my friend (male)',
          'mi amiga': 'my friend (female)',
          'el profesor': 'the teacher',
        },
      },
    ],
    targetChunks: [
      'Me llamo',
      'Yo soy',
      'Mi nombre es',
      'Buenos dias',
      'Hola',
    ],
  },

  // ── SB-02: Ser y Estar (To Be) ──────────────────────────────
  {
    id: 'sb-es-a1-02',
    title: 'Ser y Estar',
    columns: [
      {
        name: 'Sujeto (Subject)',
        options: ['Yo', 'Tu', 'El/Ella', 'Nosotros', 'Ellos/Ellas'],
        translations: {
          'Yo': 'I',
          'Tu': 'You (informal)',
          'El/Ella': 'He/She',
          'Nosotros': 'We',
          'Ellos/Ellas': 'They',
        },
      },
      {
        name: 'Verbo (Verb)',
        options: ['soy/eres/es', 'estoy/estas/esta', 'no soy', 'no estoy'],
        translations: {
          'soy/eres/es': 'am/are/is (permanent)',
          'estoy/estas/esta': 'am/are/is (temporary)',
          'no soy': 'am not (permanent)',
          'no estoy': 'am not (temporary)',
        },
      },
      {
        name: 'Complemento (Complement)',
        options: ['alto/a', 'cansado/a', 'en casa', 'estudiante', 'de Madrid', 'contento/a'],
        translations: {
          'alto/a': 'tall',
          'cansado/a': 'tired',
          'en casa': 'at home',
          'estudiante': 'student',
          'de Madrid': 'from Madrid',
          'contento/a': 'happy',
        },
      },
    ],
    targetChunks: [
      'soy alto',
      'estoy cansado',
      'es estudiante',
      'esta en casa',
      'no estoy contento',
    ],
  },

  // ── SB-03: Gustos y Preferencias (Likes & Preferences) ──────
  {
    id: 'sb-es-a1-03',
    title: 'Gustos y Preferencias',
    columns: [
      {
        name: 'Sujeto (Subject)',
        options: ['A mi', 'A ti', 'A el/ella', 'A nosotros', 'A ellos'],
        translations: {
          'A mi': 'To me',
          'A ti': 'To you',
          'A el/ella': 'To him/her',
          'A nosotros': 'To us',
          'A ellos': 'To them',
        },
      },
      {
        name: 'Verbo (Verb)',
        options: ['me gusta', 'te gusta', 'le gusta', 'nos gusta', 'les gusta'],
        translations: {
          'me gusta': 'I like',
          'te gusta': 'you like',
          'le gusta': 'he/she likes',
          'nos gusta': 'we like',
          'les gusta': 'they like',
        },
      },
      {
        name: 'Complemento (Object)',
        options: ['el cafe', 'la musica', 'los libros', 'viajar', 'estudiar', 'el espanol'],
        translations: {
          'el cafe': 'coffee',
          'la musica': 'music',
          'los libros': 'books',
          'viajar': 'traveling',
          'estudiar': 'studying',
          'el espanol': 'Spanish',
        },
      },
      {
        name: 'Intensificador (Intensifier)',
        options: ['mucho', 'bastante', 'un poco', 'nada'],
        translations: {
          'mucho': 'a lot',
          'bastante': 'quite',
          'un poco': 'a little',
          'nada': 'not at all',
        },
      },
    ],
    targetChunks: [
      'me gusta el cafe',
      'me gusta mucho',
      'no me gusta nada',
      'te gusta la musica',
    ],
  },

  // ── SB-04: Rutina Diaria (Daily Routine) ────────────────────
  {
    id: 'sb-es-a1-04',
    title: 'Mi Rutina Diaria',
    columns: [
      {
        name: 'Sujeto (Subject)',
        options: ['Yo', 'Tu', 'El/Ella', 'Nosotros'],
        translations: {
          'Yo': 'I',
          'Tu': 'You',
          'El/Ella': 'He/She',
          'Nosotros': 'We',
        },
      },
      {
        name: 'Adverbio (Adverb)',
        options: ['siempre', 'nunca', 'a veces', 'normalmente', 'cada dia'],
        translations: {
          'siempre': 'always',
          'nunca': 'never',
          'a veces': 'sometimes',
          'normalmente': 'normally',
          'cada dia': 'every day',
        },
      },
      {
        name: 'Verbo (Verb)',
        options: ['como', 'bebo', 'estudio', 'trabajo', 'leo', 'veo'],
        translations: {
          'como': 'I eat',
          'bebo': 'I drink',
          'estudio': 'I study',
          'trabajo': 'I work',
          'leo': 'I read',
          'veo': 'I see/watch',
        },
      },
      {
        name: 'Complemento (Object)',
        options: ['pan', 'agua', 'espanol', 'en casa', 'un libro', 'la tele'],
        translations: {
          'pan': 'bread',
          'agua': 'water',
          'espanol': 'Spanish',
          'en casa': 'at home',
          'un libro': 'a book',
          'la tele': 'TV',
        },
      },
    ],
    targetChunks: [
      'siempre como pan',
      'nunca bebo cafe',
      'a veces estudio espanol',
      'normalmente trabajo en casa',
      'cada dia leo un libro',
    ],
  },

  // ── SB-05: La Familia (Family) ──────────────────────────────
  {
    id: 'sb-es-a1-05',
    title: 'Mi Familia',
    columns: [
      {
        name: 'Determinante',
        options: ['Mi', 'Tu', 'Su', 'Nuestra'],
        translations: {
          'Mi': 'My',
          'Tu': 'Your',
          'Su': 'His/Her',
          'Nuestra': 'Our',
        },
      },
      {
        name: 'Sustantivo (Noun)',
        options: ['familia', 'padre', 'madre', 'hermano', 'hermana', 'abuelo', 'abuela'],
        translations: {
          'familia': 'family',
          'padre': 'father',
          'madre': 'mother',
          'hermano': 'brother',
          'hermana': 'sister',
          'abuelo': 'grandfather',
          'abuela': 'grandmother',
        },
      },
      {
        name: 'Verbo (Verb)',
        options: ['es', 'tiene', 'se llama', 'vive en'],
        translations: {
          'es': 'is',
          'tiene': 'has',
          'se llama': 'is called',
          'vive en': 'lives in',
        },
      },
      {
        name: 'Adjetivo/Lugar',
        options: ['grande', 'pequena', 'amable', 'alto/a', 'Madrid', 'Barcelona'],
        translations: {
          'grande': 'big',
          'pequena': 'small',
          'amable': 'kind',
          'alto/a': 'tall',
          'Madrid': 'Madrid',
          'Barcelona': 'Barcelona',
        },
      },
    ],
    targetChunks: [
      'Mi familia es grande',
      'Mi padre tiene',
      'Mi madre se llama',
      'Mi hermano vive en Madrid',
    ],
  },
];

export default SPANISH_A1_SENTENCE_BUILDERS;
